import { Location } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { hasAccess } from "app/access-control/permission-verification";
import { ProviderPanel } from "app/access-control/permissions";
import {
  Events,
  IonicPage,
  LoadingController,
  ModalController,
  NavController,
  NavParams,
  PopoverController,
  ToastController,
} from "ionic-angular";
import { BaseInput } from "ionic-angular/umd/util/base-input";
import { TabDetails } from "models/components/tabDetails";
import SortOptions from "models/content-wrapper/sort-options";
import { ActionBase } from "models/order-management/actionBase";
import { CanceledAction } from "models/order-management/canceledAction";
import { OrderErrorResponse } from "models/order-management/errorResponse";
import {
  getHistoryDetailSummary,
  HistoryDetailSummary,
} from "models/order-management/historyDetailSummary";
import {
  Action,
  DiscontinuedAction,
  DropdownItem,
  Order,
  OrderSignatureRequest,
  OrderSignatureRequests,
} from "models/order-management/models";
import { OrderResponse } from "models/order-management/orderResponse";
import { OrderSearchRequest } from "models/order-management/orderSearchRequest";
import { OrderTypeConfiguration } from "models/order-management/orderTypeConfiguration";
import { isProfessionalProvider } from "models/resource-management/company-resource-type-guards";
import CompanyResourceType from "models/resource-management/company-resource-type.enum";
import CredentialedResource from "models/resource-management/credentialed-resource";
import { EditViewPopoverPage } from "navigation/components/edit-view-popover/edit-view-popover";
import { NavigationService } from "navigation/navigation.service";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { MedicationDataService } from "providers/order-data-service/medications-data-service";
import { OrderManagementDataService } from "providers/order-management-data-service/order-management-data-service";
import { PlanOfCareDataService } from "providers/poc-data-service/poc-data-service";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { Observable, Subject, Subscription } from "rxjs";
import { of } from "rxjs/observable/of";
import {
  catchError,
  distinctUntilChanged,
  filter,
  mergeMap,
  switchMap,
} from "rxjs/operators";
import { AccessControlService } from "../../../app/access-control/access-control-service";
import { AuthService } from "../../../app/auth/auth.service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { UsersService } from "../../../providers/users-service/users.service";
import { BasePage } from "../../base-page";
import { getOrderTypeDisplayName, OrderTypeSubtype } from "../order-utils";
import { ESignSelectedOrdersPopup } from "./e-sign-selected-orders/e-sign-selected-orders";

const providerPanelPageSize = 100;

interface IToggle {
  value: boolean;
}

/**
 * @interface PatientOrdersFilter - The `PatientOrdersFilter` interface is designed to store filter and sort
 * configurations at the Patient level.
 * @member sortColumn - The column name that is actively being used to sort this
 * particular patient's order data.
 * @member sortDirection - The direction - "asc" | "desc" - of the patient's
 * sorted order data.
 * @member showPatientOrders - Indicates if the UI should show the orders
 * attributed to this patient.
 * @member patientName - The most recent name attributed to the specific
 * patient.
 * @member externalPatientChartId - The externally facing numeric identifier
 * attributed to the specific patient.
 */
interface PatientOrdersFilter {
  sortColumn: string;
  sortDirection: "asc" | "desc";
  showPatientOrders: boolean;
  // These two fields can potentially be removed
  patientChecked: boolean;
  ordersChecked: { [k: string]: false | TailoredOrderResponse };
  patientName: string;
  externalPatientChartId: number;
}

/**
 * @typedef TailoredOrderResponse - The `TailoredOrderResponse` type is
 * designed to store individual order data for a specific patient.
 * @member canceledAction - Data related to any canceled actions taken on this
 * specific patient order; used as a filter value for the "Discontinued /
 * Rejected" toggles.
 * @member createdAction - Data related to any created actions taken on this
 * specific patient order.
 * @member discontinueDate - Data related to any discontinued actions taken on
 * this specific patient order; used as a filter value for the "Discontinued /
 * Rejected" toggles.
 * @member showOrderData - It shows if the order created data is not signed.
 * @member showDiscontinuedData - It shows if the order discontinued data is not signed.
 * @member nameDescription - The name / description of the specific order.
 * @member orderType - The type of the specific order.
 * @member signedStatus - The status of the order (signed / unsigned); used as a
 * filter value for the "Signed" toggles.
 * @member startDate - The date the order is scheduled to be "in effect".
 */
type TailoredOrderResponse = {
  canceledAction: CanceledAction;
  discontinuedAction: DiscontinuedAction;
  createdAction: ActionBase;
  createdBy: string;
  discontinueDate: string;
  showCreatedData: boolean;
  showDiscontinuedData: boolean;
  nameDescription: string;
  orderType: string;
  orderSubtype: string;
  signedStatus: OrderSignatureRequest.SignedStatusEnum;
  startDate: string;
  orderId: string;
  patientName: string;
  externalPatientChartId: number;
  showDetails: boolean;
  signatureRequests: OrderSignatureRequests;
};

/**
 * @typedef PatientData - The `PatientData` type is designed to store
 * demographic and order related data for a specific patient.
 * @member externalPatientChartId - The externally facing numeric identifier
 * attributed to the specific patient.
 * @member patientChartId - The internally facing numeric identifier attributed
 * to the specific patient.
 * @member mostRecentPatientName - The most recent name attributed to the
 * specific patient.
 * @member mostRecentOrderDate - The most recent order date attributed to the
 * specific patient.
 * @member orders - A list of all orders that are attributed to a specific
 * patient.
 */
type PatientData = {
  externalPatientChartId: number;
  patientChartId: number;
  mostRecentPatientName: string;
  mostRecentOrderDate: string;
  orders: TailoredOrderResponse[];
};

/**
 * @typedef PatientLevelData - The `PatientLevelData` type is designed to store
 * all the relevant patient data to be displayed on the page in a single state
 * object.
 * @member patientChartId - The internally facing numeric identifier attributed
 * to the specific patient; serves as a key to use to reference a specific
 * patient's `PatientData` object.
 */
type PatientLevelData = {
  [patientChartId: number]: PatientData;
};

/**
 * @typedef PatientLevelFilters - The `PatientLevelData` type is designed to
 * store all the relevant patient filter data to be displayed on the page in a
 * single state object.
 * @member patientChartId - The internally facing numeric identifier attributed
 * to the specific patient; serves as a key to use to reference a specific
 * patient's `PatientOrdersFilter` object.
 */
type PatientLevelFilters = {
  [patientChartId: number]: PatientOrdersFilter;
};

/**
 * @typedef PatientNameAndChartIds - The `PatientNameAndChartIds` type is
 * designed to store all of the relevant patient demographic data to be
 * displayed on the page in a single state object; primarily used as a reference
 * and for sorting patient data globally.
 * @member externalPatientChartId - The externally facing numeric identifier
 * attributed to the specific patient.
 * @member mostRecentPatientName - The most recent name attributed to the
 * specific patient.
 * @member patientChartId - The internally facing numeric identifier attributed
 * to the specific patient.
 * @member showPatientOrderRecords - Indicates if the UI should show the orders
 * attributed to this patient.
 */
type PatientNameAndChartIds = {
  externalPatientChartId: number;
  mostRecentPatientName: string;
  patientChartId: number;
  showPatientOrderRecords: boolean;
};

interface ActionItem {
  label: string;
  value: string;
  /**
   * url for the icon
   */
  src: string;
}

export interface OrderIdAndSignatureRequest {
  orderId: string;
  signatureRequests: Action.SignatureRequestEnum[];
}

@IonicPage({
  segment: "provider/order-panel",
})
@Component({
  selector: "provider-panel",
  templateUrl: "provider-panel.html",
})
//CompanyPage
export class ProviderPanelPage extends BasePage implements OnDestroy, OnInit {
  /****************************************************************************
   * -------------------------- Instance Variables -------------------------- *
   ****************************************************************************/
  @ViewChild("selectedActions") selectedActions;
  @ViewChild("orderingProvider") orderingProvider;
  @ViewChild("hiddenInput") hiddenInput;
  @Output() handleIFrameBannerErrorCloseButtonClickEvent: EventEmitter<any> =
    new EventEmitter<any>();

  orders: OrderResponse[] = [];
  showEPrescribeSubscription: Subscription = null;
  spinnerCounter = 0;
  spinner = null;
  numberOfOrdersChecked = 0;
  selectedAction: string | null = null;
  selectedActionsOptions: ActionItem[] = [
    {
      label: "e-Sign All Orders",
      value: "eSignAllOrders",
      src: "assets/svg/provider-panel/E-Sign%20All%20Orders.svg",
    },
    {
      label: "e-Sign Selected Orders",
      value: "eSignSelectedOrders",
      src: "assets/svg/provider-panel/E-Sign%20Selected%20Orders.svg",
    },
  ];
  credentialedResource: CredentialedResource;
  patientLevelData: PatientLevelData;
  patientLevelFilters: PatientLevelFilters;
  globalSorts: PatientNameAndChartIds[];
  isDiscontinuedRejectedToggleOn: boolean = true;
  isSignedToggleOn: boolean = true;
  orderTypeConfigurations: Array<OrderTypeConfiguration> = [];
  search: string = null;
  orderSearchDetails: OrderSearchRequest = null;
  globalSortColumn: string = "mostRecentPatientName";
  // CO-3363: paired with the dynamicSort direction fix. The inverted string
  // comparator meant a "desc" default actually rendered patients A→Z; correcting
  // the comparator without correcting this default would have silently flipped
  // the landing view to Z→A. "asc" keeps the rendered order exactly as it is
  // today AND makes the header caret (which points up for "asc") tell the truth.
  globalSortDirection: "asc" | "desc" = "asc";
  isFilterFromPanelLevel: boolean = true;
  searchField = new FormControl("");
  ePrescribeAvailable = false;
  orderToShowDetails: Partial<OrderResponse> = {};
  discontinuedDetails: boolean = true;
  historyDetails: HistoryDetailSummary[] = [];
  formattedTotalOrderCount: string;
  // Pagination state (NOVA-1560)
  pageSize: number = providerPanelPageSize;
  currentPage: number = 0;
  totalHits: number = 0;
  totalPageCount: number = 1;
  pageTabs: TabDetails[] = [
    {
      label: "eRx",
      id: 0,
      showTabPage: true,
    },
    {
      label: "Orders",
      id: 1,
      showTabPage: false,
    },
  ];
  activatedTabId: number = 0;

  // Ordering Provider Dropdown
  isSupportUser = false;
  providerDropdownItems$: Observable<DropdownItem[]>;
  providerQuery$ = new Subject<string>();
  orderingProviderId: number;
  providerForm: FormGroup;
  get isReadOnly() {
    // read only if credentialedResource not loaded or provider is selected and doesn't match the logged in user
    return (
      !this.credentialedResource ||
      (this.orderingProviderId &&
        this.orderingProviderId !== this.credentialedResource.getProviderId())
    );
  }

  constructor(
    private planOfCareDataService: PlanOfCareDataService,
    protected accessControlService: AccessControlService,
    protected navCtrl: NavController,
    protected navParams: NavParams,
    protected userService: UsersService,
    protected stateManagementService: StateManagementService,
    public navSvc: NavigationService,
    private navBarService: NavBarServiceProvider,
    private orderManagementDataService: OrderManagementDataService,
    private toastCtrl: ToastController,
    public loadingCtrl: LoadingController,
    private events: Events,
    private utilityService: UtilityService,
    private resourceManagementService: ResourceManagementService,
    private authSvc: AuthService,
    private popoverCtrl: PopoverController,
    private elementRef: ElementRef,
    private tabsRef: ElementRef,
    public modalCtrl: ModalController,
    private medicationDataService: MedicationDataService,
    private fb: FormBuilder,
    private location: Location
  ) {
    super(stateManagementService);
    this.providerForm = this.fb.group({
      orderingProvider: this.fb.control(null),
    });
  }

  /****************************************************************************
   * --------------------- Angular Methods - Boilerplate -------------------- *
   ****************************************************************************/

  ngOnDestroy() {
    super.ngOnDestroy();
    // CO-3363: `showEPrescribe` is a BehaviorSubject that never completes, so
    // every visit to the panel added another live subscription that kept the
    // destroyed page — and its order data — reachable. Ionic 3 keeps pages in the
    // nav stack, so this accumulated across a session.
    if (this.showEPrescribeSubscription) {
      this.showEPrescribeSubscription.unsubscribe();
      this.showEPrescribeSubscription = null;
    }
  }

  ngOnInit(): void {
    this.accessControlService
      .getPermission([ProviderPanel.DashboardSupportView])
      .subscribe((data) => {
        this.isSupportUser = hasAccess(
          ProviderPanel.DashboardSupportView,
          data.permissions
        );
      });
    this.initProviderSearch();
  }

  /****************************************************************************
   * ------------------ Ionic Global Methods - Boilerplate ------------------ *
   ****************************************************************************/

  ionViewDidLoad() {
    this.stateManagementService.setAssessmentType("Provider");
    this.stateManagementService.setSectionId("Order Panel");
    this.showEPrescribeSubscription = this.stateManagementService.showEPrescribe
      .pipe(distinctUntilChanged())
      .subscribe((value) => {
        if (value) {
          this.medicationDataService.getOrganization().subscribe(
            () => {
              // Organization found; ePrescribe is available
              this.ePrescribeAvailable = true;
            },
            // Organization not found (404); ePrescribe not available. Value is unchanged (`false`).
            null,
            () => {
              // Finally, configure page based on results
              this.setSectionButtonsAndViewBasedOnEPrescribeToggle();
            }
          );
        } else {
          this.setSectionButtonsAndViewBasedOnEPrescribeToggle();
        }
      });
    this.setSectionButtonsAndViewBasedOnEPrescribeToggle();
  }

  ionViewCanEnter() {
    return this.authSvc.isAuthenticated();
  }

  ionViewWillEnter(): void {
    this.getOrderTypeConfigurations();
    this.getUserInformation();
    // this.setViewBasedOnEPrescribeToggle();
  }

  /****************************************************************************
   * -------------------------- Page Class Methods -------------------------- *
   ****************************************************************************/

  setSectionButtonsAndViewBasedOnEPrescribeToggle(): void {
    this.setSectionButtonsBasedOnEPrescribeToggle();
    this.setViewBasedOnEPrescribeToggle();
  }

  getOrderTypeConfigurations(): void {
    this.orderManagementDataService
      .getOrderTypes()
      .subscribe((orderTypeConfigurations) => {
        this.orderTypeConfigurations = orderTypeConfigurations;
      }, OrderErrorResponse.handleError(this.toastCtrl, "Could not get order type configurations."));
  }

  getUserInformation() {
    // CO-3363: `AuthService.getUser()` is an in-memory read that is never
    // rehydrated, so it is null on any cold deep link to `#/provider/order-panel`
    // (browser refresh, bookmark, link from an email). Dereferencing `.userId`
    // threw before the request was ever made, which left the panel permanently
    // empty with no error surfaced. Fail loudly instead.
    const user = this.authSvc.getUser();
    if (!user) {
      this.toastCtrl
        .create({
          message:
            "Unable to determine the signed-in user. Please reload the page.",
          duration: 6000,
          position: "below",
          cssClass: "error",
        })
        .present();
      return;
    }
    this.resourceManagementService
      .getCredentialedResourceByExternalId(user.userId)
      .subscribe((provider) => {
        this.credentialedResource = provider;
        this.loadData();
      }, OrderErrorResponse.handleError(this.toastCtrl, "Failed to retrieve user information."));
  }

  initProviderSearch() {
    this.providerDropdownItems$ = this.providerQuery$.pipe(
      distinctUntilChanged(),
      filter((query) => !query || ![1, 2].includes(query.length)), // do nothing if 1 or 2 characters
      switchMap((query: string | null) => {
        // null -> clear, '' -> field cleared by backspace
        if (!query || query.length === 0) {
          return of([]);
        }
        return this.resourceManagementService
          .getCompanyResourceSummaries(
            0,
            100,
            query ? query : undefined,
            new SortOptions("last_name", "asc"),
            CompanyResourceType.PROVIDER
          )
          .pipe(
            mergeMap((providerList) =>
              of(
                providerList.content.map((provider) => {
                  return {
                    label: provider.displayName,
                    value: `${provider.externalId}`,
                  };
                })
              )
            ),
            catchError(() => of([]))
          );
      })
    );
  }

  onProviderSelected(event: DropdownItem) {
    if (!event) {
      this.orderingProviderId = undefined;
    } else {
      this.orderingProviderId = +event.value;
    }
    this.currentPage = 0;
    this.loadData();
  }

  // Hackish fix to show keyboard on IOS / Safari
  onClick(event: PointerEvent) {
    this.hiddenInput.setFocus();
    const element = event.target as HTMLElement;
    // Clicked on input
    if (element.constructor.name === "HTMLInputElement") {
      element.focus();
    }
    // Clicked on a parent of input
    const input = element.querySelector("input");
    if (input) {
      input.focus();
    }
  }

  getCurrentOrderingProviderId(): number {
    // CO-3363: `credentialedResource` stays undefined whenever the resource
    // lookup fails (its error handler only toasts) or is still in flight. Every
    // caller — loadData, createSearchRequestBody, populatePatientLevelDataObject,
    // getUnsignedAllOrders, getTotalOrderCounter — reached the unguarded
    // dereference, so clearing the ordering-provider select before the lookup
    // resolved threw instead of simply loading nothing. loadData already treats a
    // falsy id as "not ready yet".
    if (this.orderingProviderId) {
      return this.orderingProviderId;
    }
    return this.credentialedResource
      ? this.credentialedResource.getProviderId()
      : undefined;
  }

  /****************************************************************************
   * ------------------------ Page Navigation Methods ----------------------- *
   ****************************************************************************/

  navigate(entity: string) {
    this.navCtrl.push(entity);
  }

  exitProviderPanel() {
    this.location.back();
  }

  /****************************************************************************
   * ----------------------------- Helper Methods --------------------------- *
   ****************************************************************************/

  setSectionButtonsBasedOnEPrescribeToggle() {
    if (this.ePrescribeAvailable) {
      document.documentElement.style.setProperty(
        "--sectionButtons-justify-content-value",
        "space-between"
      );
      document.documentElement.style.setProperty(
        "--order-panel-card-content-height",
        "88%"
      );
    } else {
      document.documentElement.style.setProperty(
        "--sectionButtons-justify-content-value",
        "flex-end"
      );
      document.documentElement.style.setProperty(
        "--order-panel-card-content-height",
        "91%"
      );
    }
  }

  setViewBasedOnEPrescribeToggle() {
    if (!this.ePrescribeAvailable) {
      this.activatedTabId = 1;
    } else {
      this.activatedTabId = 0;
    }
  }

  loadData(): void {
    // No data can be loaded until the credentialed resource is ready
    if (!this.getCurrentOrderingProviderId()) {
      return;
    }

    // Reset State Variables
    this.orders = [];
    this.globalSorts = [];
    this.patientLevelData = {};
    this.patientLevelFilters = {};
    // CO-3363: `patientLevelFilters` holds the per-order checkbox state, so
    // wiping it above drops every selection — but the counter that gates the
    // Actions control lived on separately. Paging, searching or switching
    // provider therefore left "Actions for e-Sign Orders" enabled over an empty
    // selection, and picking an action toasted "No unsigned orders selected."
    // The counter is derived from the same state it summarises; reset together.
    this.numberOfOrdersChecked = 0;

    // Create the search request body + start the UI spinner to indicate loading
    this.createSearchRequestBody();
    this.increaseSpinnerCount("Loading orders ...");

    // Retrieve the patient order data
    this.orderManagementDataService
      .searchOrders(this.orderSearchDetails)
      .finally(() => this.decreaseSpinnerCount())
      .subscribe((OpenSearchResponse) => {
        if (OpenSearchResponse && OpenSearchResponse.hits) {
          this.orders = OpenSearchResponse.hits;
          this.totalHits = OpenSearchResponse.totalHits || 0;
          this.totalPageCount = Math.max(
            1,
            Math.ceil(this.totalHits / this.pageSize)
          );
          this.formattedTotalOrderCount = `(${this.totalHits})`;
          this.patientLevelData = this.populatePatientLevelDataObject(
            this.orders
          );
          this.sortPatientOrdersByStartDateInAscendingOrder();
        } else {
          this.orders = [];
          this.totalHits = 0;
          this.totalPageCount = 1;
          this.formattedTotalOrderCount = "";
        }
      }, OrderErrorResponse.handleError(this.toastCtrl, "Error loading orders."));
  }

  sortPatientOrdersByStartDateInAscendingOrder() {
    for (let patientId of Object.keys(this.patientLevelData)) {
      this.patientLevelData[patientId].orders.sort(
        this.dynamicSort("startDate", "asc")
      );
    }
  }

  /**
   * The `populatePatientLevelDataObject` method accepts a single parameter that
   * is an array of `OrderResponse` type - `orders`.
   *
   * The method uses the `.reduce()` method to populate the `patientLevelData`
   * state object.
   *
   * @param orders - An array of `OrderResponse` objects.
   * @returns - The values that should be contained within the `patientLevelData`
   * state object.
   */
  populatePatientLevelDataObject(orders: OrderResponse[]): any {
    const providerId = this.getCurrentOrderingProviderId();
    return orders.reduce((acc, val) => {
      // `.reduce()` Method State
      const patientChartId = val.patientChartId;
      const existingPatient = acc[patientChartId];
      const newPatientOrder: TailoredOrderResponse = {
        canceledAction: val.canceledAction,
        createdAction: val.createdAction,
        discontinuedAction: val.discontinuedAction,
        // CO-3363: both of these were unguarded. Orders come straight from
        // OpenSearch and the model marks signatureRequests optional, so a single
        // row missing either field threw inside this reduce — which runs inside
        // loadData's subscribe, so the whole grid came back EMPTY rather than
        // skipping the one bad row. The template already optional-chains
        // createdAction; this now matches it.
        createdBy: val.createdAction ? val.createdAction.displayName : "",
        discontinueDate: val.discontinueDate,
        showCreatedData: Order.isCreatedOrderSignable(val, providerId),
        showDiscontinuedData: Order.isDiscontinuedOrderSignable(
          val,
          providerId
        ),
        nameDescription: val.nameDescription,
        orderType: val.orderType,
        orderSubtype: val.orderSubtype,
        signedStatus:
          val.signatureRequests && val.signatureRequests.created
            ? val.signatureRequests.created.signedStatus
            : undefined,
        startDate: val.startDate,
        orderId: val.orderId,
        patientName: val.patientName,
        externalPatientChartId: val.externalPatientChartId,
        showDetails: false,
        signatureRequests: val.signatureRequests,
      };

      // `.reduce()` Method Behavior
      if (!existingPatient) {
        const newPatientData: PatientData = {
          externalPatientChartId: val.externalPatientChartId,
          mostRecentPatientName: val.patientName,
          mostRecentOrderDate: val.startDate,
          patientChartId: val.patientChartId,
          orders: [newPatientOrder],
        };
        acc[patientChartId] = newPatientData;
        this.populatePatientLevelFiltersObject(newPatientData);
        this.populateGlobalSortsObject(newPatientData);
        this.sortByGlobalColumn();
      } else {
        this.validateMostRecentPatientName(acc[patientChartId], val);
        acc[patientChartId].orders.push(newPatientOrder);
      }
      return acc;
    }, {});
  }

  /**
   * The `validateMostRecentPatientName` method accepts a `PatientData` and an
   * `OrderResponse` object as parameters - `patientLevelData` and `order,
   * respectively.
   *
   * The method evaluates whether the recorded `mostRecentPatientName` property
   * has the most recent patient name based on the `mostRecentOrderDate`
   * property. If the date is newer than the stored date, then the name is
   * replaced with the appropriate name - otherwise it is left alone.
   *
   * @param patientLevelData - A `PatientData` object that will be used to
   * create a new patient.
   * @param order - An `OrderResponse` object that will be used to either create
   * a new patient or will be added to an existing patient.
   */
  validateMostRecentPatientName(
    patientLevelData: PatientData,
    order: OrderResponse
  ): void {
    // Method State
    const oldDate = new Date(patientLevelData.mostRecentOrderDate);
    const potentialNewDate = new Date(order.startDate);

    // Method Behavior
    if (potentialNewDate > oldDate) {
      patientLevelData.mostRecentOrderDate = order.startDate;
      patientLevelData.mostRecentPatientName = order.patientName;
    }
  }

  /**
   * The `populateGlobalSortsObject` method accepts a single `PatientData`
   * parameter - `newPatient`.
   *
   * The method creates a `newGlobalSortsEntry` object based on the data it has
   * been passed and then pushes it onto the `globalSorts` state variable.
   *
   * @param newPatient - A `PatientData` object that will be used to create a
   * new entry in the `globalSorts` state object.
   */
  populateGlobalSortsObject(newPatient: PatientData): void {
    // Method State
    const newGlobalSortsEntry: PatientNameAndChartIds = {
      externalPatientChartId: newPatient.externalPatientChartId,
      mostRecentPatientName: newPatient.mostRecentPatientName,
      patientChartId: newPatient.patientChartId,
      showPatientOrderRecords: true,
    };

    // Method Behavior
    this.globalSorts.push(newGlobalSortsEntry);
  }

  /**
   * The `populatePatientLevelFiltersObject` method accepts a single `PatientData`
   * parameter - `newPatient`.
   *
   * The method creates a new entry in the `patientLevelFilters` state object
   * based on the values passed in the `newPatient` parameter.
   *
   * @param newPatient - A `PatientData` object that will be used to create a
   * new entry in the `patientLevelFilters` state object.
   */
  populatePatientLevelFiltersObject(newPatient: PatientData): void {
    this.patientLevelFilters[newPatient.patientChartId] = {
      showPatientOrders: false,
      sortColumn: "startDate",
      sortDirection: "asc",
      patientChecked: false,
      ordersChecked: {},
      patientName: newPatient.mostRecentPatientName,
      externalPatientChartId: newPatient.externalPatientChartId,
    };
  }

  createSearchRequestBody() {
    this.orderSearchDetails = {
      ...(this.ePrescribeAvailable && {
        // Exclude eRx Medication Orders
        mustNotFields: [
          {
            bool: {
              must: [
                {
                  match: {
                    OrderType: "Medication",
                  },
                },
                {
                  match: {
                    OrderSubtype: "ERX",
                  },
                },
              ],
              mustNot: {
                exists: {
                  field: "SignatureRequests.Discontinued",
                },
              },
            },
          },
        ],
      }),
      includeDiscontinued: true,
      orderingProviderId: this.getCurrentOrderingProviderId(),
      providerPanel: true,
      queryString: this.search === "" ? undefined : this.search,
      from: this.currentPage * this.pageSize,
      size: this.pageSize,
    };
  }

  increaseSpinnerCount(message: string) {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0 && this.spinner) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  /****************************************************************************
   * -------------------------- Pagination Methods ------------------------- *
   ****************************************************************************/

  onPageChange(newPageNum: number): void {
    this.setPage(newPageNum);
  }

  setPage(page: number): void {
    if (
      page >= 0 &&
      page <= this.totalPageCount - 1 &&
      page !== this.currentPage
    ) {
      this.currentPage = page;
      this.loadData();
    }
  }

  firstPage(): void {
    this.setPage(0);
  }

  prevPage(): void {
    this.setPage(this.currentPage - 1);
  }

  nextPage(): void {
    this.setPage(this.currentPage + 1);
  }

  lastPage(): void {
    this.setPage(this.totalPageCount - 1);
  }

  /****************************************************************************
   * -------------------------- Search Bar Methods -------------------------- *
   ****************************************************************************/

  getItemsBySearchFilter() {
    this.currentPage = 0;
    this.loadData();
  }

  clearSearchBar() {
    this.search = null;
    this.currentPage = 0;
    this.loadData();
  }

  /****************************************************************************
   * -------------------------- Click Event Methods ------------------------- *
   ****************************************************************************/

  onArrowClicked(patient: any): void {
    patient.showPatientOrderRecords = !patient.showPatientOrderRecords;
  }

  onArrowClickedOrder(order: OrderResponse): void {
    for (let key in this.patientLevelData) {
      this.patientLevelData[key].orders.forEach((tailoredOrder) => {
        tailoredOrder.showDetails =
          tailoredOrder.orderId === order.orderId
            ? !tailoredOrder.showDetails
            : false;
      });
    }

    const orderTypeConfiguration = this.orderTypeConfigurations.find(
      (orderTypeConfiguration) =>
        orderTypeConfiguration.orderType == order.orderType
    );

    this.orderToShowDetails = this.orders.find(
      (o) => o.orderId === order.orderId
    );

    this.historyDetails = getHistoryDetailSummary(
      this.orderToShowDetails,
      orderTypeConfiguration
    );
  }

  getOrderTypeDisplayName(orderTypeSubtype: OrderTypeSubtype) {
    let orderTypeDisplayName = getOrderTypeDisplayName(
      orderTypeSubtype,
      this.orderTypeConfigurations
    );
    // Only in provider panel: we append non-eRx to Medication orders with no subtype
    if (
      orderTypeSubtype.orderType === "MEDICATION" &&
      typeof orderTypeSubtype.orderSubtype === "undefined"
    ) {
      orderTypeDisplayName = `${orderTypeDisplayName} - non-eRx`;
    }
    return orderTypeDisplayName;
  }

  sendToOrderEntry(internalPatientChartId, externalPatientChartId) {
    this.navSvc.setSessionStorage(
      externalPatientChartId,
      internalPatientChartId
    );
    this.stateData.setSectionId(null);
    this.planOfCareDataService.setPatientChartId(externalPatientChartId);
    this.planOfCareDataService.setAssessmentId(null);
    this.navSvc.goToInternalPage("OrderEntryOMPage", {
      patChartId: externalPatientChartId,
      externalPatientChartId: internalPatientChartId,
      assessmentId: null,
      assessmentUuid: null,
    });
  }

  sendToProfilePage(internalPatientChartId, externalPatientChartId) {
    this.navSvc
      .resolvePatientSessionFromExternalId(externalPatientChartId)
      .then((chart) => {
        const section =
          this.navBarService.convertStatusUrlSection(chart?.patientStatus) ||
          "patient";
        window.location.href = `/#/referral-tabs/${section}/patient-details/${internalPatientChartId}`;
      });
  }

  /****************************************************************************
   * --------------- <ion-toggle> (ionChange) Behavior Methods -------------- *
   ****************************************************************************/

  onExpandOrCollapseAll(view: "collapse" | "expand") {
    const value = view == "expand";
    for (let patient of this.globalSorts) {
      patient.showPatientOrderRecords = value;
    }
    this.isFilterFromPanelLevel = true;
  }

  onSignAllOrders() {
    const allUnsignedOrders = this.getUnsignedAllOrders();
    if (allUnsignedOrders.length === 0) {
      const toast = this.toastCtrl.create({
        message: "No unsigned orders.",
        duration: 3000,
        position: "below",
        cssClass: "error",
      });
      toast.present();
      return;
    }
    let modal = this.modalCtrl.create(
      ESignSelectedOrdersPopup,
      {
        orders: allUnsignedOrders,
        provider: this.credentialedResource,
        popUpTitle: true,
        orderCounter: this.getTotalOrderCounter(),
      },
      {
        enableBackdropDismiss: false,
        cssClass: "e-sign-selected-orders",
      }
    );
    modal.present();
    modal.onDidDismiss(
      (data: { bulkUpdateOrderRequest: Observable<void>; cancel: boolean }) => {
        // CO-3363: a modal dismissed without a payload (`dismiss()` with no
        // argument) delivers null here, so reading `.cancel` threw and the
        // spinner below never started — the click looked like it did nothing.
        if (!data || data.cancel || !data.bulkUpdateOrderRequest) {
          return;
        }
        this.increaseSpinnerCount("Submitting e-Signatures...");
        data.bulkUpdateOrderRequest
          .finally(() => this.decreaseSpinnerCount())
          .subscribe(() => {
            const toast = this.toastCtrl.create({
              message:
                "Electronic signing in progress. This will take a moment to complete. Please refresh to see updates.",
              showCloseButton: true,
              position: "below",
              cssClass: "success",
            });
            toast.present();
          }, OrderErrorResponse.handleError(this.toastCtrl, "Failed to submit orders for electronic signature."));
      }
    );
  }
  /****************************************************************************
   * ------------------- Sorting By Global Column Methods ------------------- *
   ****************************************************************************/

  /**
   * The `getCaretState` method accepts a single required parameter -
   * `selectedColumn` (string) - and a single optional parameter -
   * `patientChartId` (number).
   *
   * The method evaluates the provided parameters and, depending on their
   * presence and / or values, assigns them to local variables within the method
   * and executes the `getCaretIonIcon` helper method, returning that method's
   * result.
   *
   * @param selectedColumn - A string value indicating the specific
   * column selected by the User for sorting the data on the screen.
   * @param patientChartId - A number value indicating the specific
   * patient's data to be sorted if the column selected was within a specific
   * patient's list of orders.
   * @returns - A string value indicating if the caret should display
   * as "ios-arrow-up" or "ios-arrow-down" within the UI.
   */
  getCaretState(selectedColumn: string, patientChartId?: number): string {
    // Method State
    let sortColumn;
    let sortDirection;

    // Method Behavior
    if (typeof patientChartId === "undefined") {
      sortColumn = this.globalSortColumn;
      sortDirection = this.globalSortDirection;
    } else {
      sortColumn = this.patientLevelFilters[patientChartId].sortColumn;
      sortDirection = this.patientLevelFilters[patientChartId].sortDirection;
    }
    return this.getCaretIonIcon(sortColumn, selectedColumn, sortDirection);
  }

  /**
   * The `getCaretIonIcon` method accepts three required parameters -
   * `sortColumn` (string), `selectedColumn` (string), and `sortDirection`
   * (string) - and returns a string value indicating if the caret should
   * display as "ios-arrow-up" or "ios-arrow-down" based on the passed values.
   *
   * @param sortColumn A string value constant indicating the most recently
   * active column used to sort page data.
   * @param selectedColumn A string value indicating the specific column
   * selected by the User for sorting the data on the screen.
   * @param sortDirection A string value indicating the direction - "asc" |
   * "desc" - that the data should be sorted by.
   * @returns A string value indicating if the caret should display as
   * "ios-arrow-up" or "ios-arrow-down" within the UI.
   */
  getCaretIonIcon(
    sortColumn: string,
    selectedColumn: string,
    sortDirection: string
  ): string {
    if (sortColumn === selectedColumn && sortDirection === "asc") {
      return "ios-arrow-up";
    }
    return "ios-arrow-down";
  }

  /**
   * CO-3363: exposes the sort state to assistive technology and to e2e as the
   * standard `aria-sort` token, so a test can assert which column is sorted and in
   * which direction without reading an ion-icon's `name`. Mirrors getCaretState:
   * pass a patientChartId for a per-patient order column, omit it for a global
   * (patient-level) column.
   */
  getAriaSort(
    selectedColumn: string,
    patientChartId?: number
  ): "none" | "ascending" | "descending" {
    let sortColumn: string;
    let sortDirection: string;
    if (typeof patientChartId === "undefined") {
      sortColumn = this.globalSortColumn;
      sortDirection = this.globalSortDirection;
    } else {
      const filters = this.patientLevelFilters
        ? this.patientLevelFilters[patientChartId]
        : undefined;
      if (!filters) {
        return "none";
      }
      sortColumn = filters.sortColumn;
      sortDirection = filters.sortDirection;
    }
    if (sortColumn !== selectedColumn) {
      return "none";
    }
    return sortDirection === "asc" ? "ascending" : "descending";
  }

  /****************************************************************************
   * ------------------- Sorting By Global Column Methods ------------------- *
   ****************************************************************************/

  /**
   * The `activateCaretAndSortGloballyByColumn` method accepts a single string
   * parameter - `selectedGlobalColumn`.
   *
   * The method uses two helper methods to activate the selected global column
   * caret and sorts the data by that particular column.
   *
   * @param selectedGlobalColumn A string value indicating the specific `column`
   * caret selected by the user.
   */
  activateCaretAndSortGloballyByColumn(selectedGlobalColumn: string): void {
    this.activateSelectedGlobalColumnCaret(selectedGlobalColumn);
    this.sortByGlobalColumn();
  }

  /**
   * The `activateSelectedGlobalColumnCaret` method accepts a single string
   * parameter - `selectedColumn`.
   *
   * - If the `selectedColumn` does not match the `sortColumn` stored in the
   * state's `globalSortColumn` for that `patientChartId`, it overwrites the
   * `globalSortColumn` with the `selectedColumn` and sets the
   * `globalSortDirection` value to "desc".
   *
   * - If the `selectedColumn` does match the `globalSortColumn` stored in the
   * state's `globalSortDirection` to the opposite value ("asc", "desc").
   *
   * @param selectedColumn A string value indicating the specifc `column` caret
   * selected by the user.
   */
  activateSelectedGlobalColumnCaret(selectedColumn: string): void {
    if (this.globalSortColumn != selectedColumn) {
      this.globalSortColumn = selectedColumn;
      this.globalSortDirection = "desc";
    } else {
      this.globalSortDirection =
        this.globalSortDirection === "asc" ? "desc" : "asc";
    }
  }

  /**
   * The `sortByGlobalColumn` method accepts no parameters.
   *
   * The method, with the help of the `dynamicSort` method, sorts through the
   * `globalSorts` array by the specific `column` and `direction` as selected by
   * the user.
   */
  sortByGlobalColumn(): void {
    // Method State
    const column = this.globalSortColumn;
    const direction = this.globalSortDirection;

    // Method Behavior
    this.globalSorts.sort(this.dynamicSort(column, direction));
  }

  /****************************************************************************
   * ------------------- Sorting By Patient Orders Methods ------------------ *
   ****************************************************************************/

  /**
   * The `activateCaretAndSortPatientOrdersByColumn` method accepts two
   * parameters - `selectedPatientOrdersColumn` (string) & `patientChartid`
   * (number).
   *
   * The method uses two helper methods to activate the selected column caret
   * within the orders section of a specific patient and sorts the data by that
   * particular column.
   *
   * @param selectedPatientOrdersColumn The string identifier
   * indicating the column caret selected by the User in the patient.
   * @param patientChartId The number value identifying the specific
   * `patientLevelFilters` object the method should check.
   */
  activateCaretAndSortPatientOrdersByColumn(
    selectedPatientOrdersColumn: string,
    patientChartId: number
  ) {
    this.activateSelectedPatientOrdersColumnCaret(
      selectedPatientOrdersColumn,
      patientChartId
    );
    this.sortByPatientOrdersColumn(patientChartId);
  }

  /**
   * The `activateSelectedPatientOrdersColumnCaret()` method accepts two
   * parameters - `selectedColumn` & `patientChartId`.
   *
   * - If the `selectedColumn` does not match the `sortColumn` stored in the
   * state's `patientLevelFilters` for that `patientChartId`, it overwrites the
   * `sortColumn` with the `selectedColumn` and sets the `sortDirection` value
   * to "desc".
   *
   * - If the `selectedColumn` does match the `sortColumn` stored in the state's
   * `patientLevelFilters` for that `patientChartId` it toggles the
   * `sortDirection` value to the opposite value ("asc", "desc").
   *
   * @param selectedColumn The string identifier indicating the
   * column caret selected by the User.
   * @param patientChartId The number value identifying the specific
   * `patientLevelFilters` object the method should check.
   */
  activateSelectedPatientOrdersColumnCaret(
    selectedColumn: string,
    patientChartId: number
  ): void {
    // Method Behavior
    if (this.patientLevelFilters[patientChartId].sortColumn != selectedColumn) {
      this.patientLevelFilters[patientChartId].sortColumn = selectedColumn;
      this.patientLevelFilters[patientChartId].sortDirection = "desc";
    } else {
      this.patientLevelFilters[patientChartId].sortDirection =
        this.patientLevelFilters[patientChartId].sortDirection === "asc"
          ? "desc"
          : "asc";
    }
  }

  /**
   * The `sortByPatientOrdersColumn` method accepts a single numeric parameter -
   * `patientChartId`.
   *
   * The method, with the help of the `dynamicSort` method, sorts through the
   * `orders` array key for a specific patient in the state `patientLevelData`
   * object.
   *
   * @param patientChartId A number indicating the specific patient whose orders
   * that need to be sorted.
   */
  sortByPatientOrdersColumn(patientChartId: number): void {
    // Method State
    const column = this.patientLevelFilters[patientChartId].sortColumn;
    const direction = this.patientLevelFilters[patientChartId].sortDirection;

    // Method Behavior
    this.patientLevelData[patientChartId].orders.sort(
      this.dynamicSort(column, direction)
    );
  }

  /****************************************************************************
   * ------------------------- Sorting Helper Method ------------------------ *
   ****************************************************************************/

  /**
   * The `dynamicSort` method accepts three string parameters - `property`,
   * `sortDirection`, and `sortType`.
   *
   * The method checks the provided `sortType`, followed by the `sortDirection`,
   * and based on that value, determines the comparison method of the sort. The
   * comparison is made between objects that possess the same `property`.
   *
   * @param property A string value indicating the property of the objects
   * that are to be compared.
   * @param sortDirection A string value indicating which direction the sort
   * should go in - "asc" | "desc".
   * @param sortType A string value - "number" | "string" - indicating the
   * kind of comparison that the method should take (`localeCompare` vs
   * subtraction).
   * @returns A numeric value indicating which of the properties should come
   * before the other one.
   */
  dynamicSort(property: string, sortDirection: string): any {
    // CO-3363: `discontinueDate` joined `startDate` here. It is an ISO date
    // string, but it was not in either the date branch or the string list, so it
    // fell through to the numeric branch and compared `"2026-07-20T…" - "…"`,
    // which is NaN. Array.sort with a NaN comparator leaves the rows untouched —
    // the Discontinue Date header flipped its caret and reordered nothing.
    // A missing discontinue date sorts last rather than poisoning the comparator.
    if (property === "startDate" || property === "discontinueDate") {
      const asTime = (row: any) => {
        const raw = row ? row[property] : null;
        // `new Date(null)` is epoch 0, not an invalid date, so a null
        // discontinue date would otherwise sort as 1 Jan 1970 — ahead of every
        // real date. Missing values are pushed to the end explicitly.
        if (raw === null || raw === undefined || raw === "") {
          return Infinity;
        }
        const time = new Date(raw).valueOf();
        return isNaN(time) ? Infinity : time;
      };
      if (sortDirection === "asc") {
        return function (a, b) {
          return asTime(a) - asTime(b);
        };
      } else {
        return function (a, b) {
          return asTime(b) - asTime(a);
        };
      }
    } else if (
      [
        "orderType",
        "nameDescription",
        "displayName",
        "signedStatus",
        "mostRecentPatientName",
        // CO-3363: the per-order rows carry `patientName`, not
        // `mostRecentPatientName`; without it a patient-name sort of orders fell
        // to the numeric branch and did nothing.
        "patientName",
        "createdBy",
      ].includes(property)
    ) {
      // CO-3363: this branch had "desc" return `a.localeCompare(b)` — which is
      // ASCENDING — while the startDate branch above maps "asc" to ascending.
      // Every string column (Patient Name, Order Type, Name/Description, Created
      // By, Signed Status) therefore sorted opposite to the caret the header drew,
      // since getCaretIonIcon points the arrow up for "asc". Directions now agree
      // across both branches. Values are coerced because a null/undefined
      // nameDescription, createdBy or signedStatus threw inside Array.sort and
      // took the whole grid down with it.
      const asString = (value: any) => (value === null || value === undefined ? "" : String(value));
      if (sortDirection === "asc") {
        return function (a, b) {
          return asString(a[property]).localeCompare(asString(b[property]));
        };
      }
      return function (a, b) {
        return asString(b[property]).localeCompare(asString(a[property]));
      };
    } else {
      if (sortDirection === "desc") {
        return function (a, b) {
          return a[property] - b[property];
        };
      }
      return function (a, b) {
        return b[property] - a[property];
      };
    }
  }

  presentEditViewPopover(
    $event,
    order: OrderResponse,
    signatureRequestType: Action.SignatureRequestEnum
  ) {
    if (!isProfessionalProvider(this.credentialedResource)) {
      let toast = this.toastCtrl.create({
        message: `Unable to retrieve user info from Resources, please confirm user has been entered is professional provider.`,
        duration: 3000,
        position: "below",
        cssClass: "error",
      });
      toast.present();
      return;
    }

    let popover = this.popoverCtrl.create(
      EditViewPopoverPage,
      {
        hideAllOrderManagment: true,
        hideOrderManagment: false,
        isPatientChart: false,
        showButtonsAsList: false,
        showMedicationEdit: false,
        onlyDelete: true,
        disabledIcon: true,
        deleteLabel: "Remove",
        orderId: order.orderId,
        orderPatientChartId: order.patientChartId,
        order: order,
        userDisplayName: this.credentialedResource.findDisplayName(),
        orderingProviderId: this.credentialedResource.getProviderId(),
        isPanelProvider: true,
        isAlreadySigned: false, // If the order is shown it is because it is not signed or rejected
        signatureRequest: signatureRequestType,
      },
      {
        cssClass: "more-options-list-popover",
      }
    );
    popover.present({
      ev: $event,
    });
    popover.onDidDismiss((mode) => {
      if (mode && mode.shouldReload) {
        this.loadData();
      }
    });
  }

  /****************************************************************************
   * ------------------------- Check boxes ------------------------ *
   ****************************************************************************/

  /**
   * Selects or deselects all orders for a particular patient
   *
   * @param event ion event containing the new value of the patient checkbox
   * @param patientChartId
   */
  selectAllOrdersForPatient(event: BaseInput<boolean>, patientChartId: number) {
    const { value } = event;
    this.patientLevelFilters[patientChartId].patientChecked = value;
    for (const order of this.patientLevelData[patientChartId].orders) {
      if (order.showCreatedData) {
        this.setAndTrackCheckboxForOrder(
          patientChartId,
          order,
          value,
          Action.SignatureRequestEnum.Created
        );
      }
      if (order.showDiscontinuedData) {
        this.setAndTrackCheckboxForOrder(
          patientChartId,
          order,
          value,
          Action.SignatureRequestEnum.Discontinued
        );
      }
    }
  }

  checkOrderForPatient(
    event: BaseInput<boolean>,
    patientChartId: number,
    orderId: string,
    signatureRequestType: Action.SignatureRequestEnum
  ) {
    const { value } = event;
    const order = this.patientLevelData[patientChartId].orders.find(
      (order) => order.orderId === orderId
    );
    if (order) {
      this.setAndTrackCheckboxForOrder(
        patientChartId,
        order,
        value,
        signatureRequestType
      );
    }
  }

  /**
   * Set the order checkbox value and keep track of how many orders are checked
   * so the actions control can be disabled when nothing is checked
   *
   * @param patientChartId
   * @param order
   * @param value
   */
  setAndTrackCheckboxForOrder(
    patientChartId: number,
    order: TailoredOrderResponse,
    value: boolean,
    signatureRequestType: Action.SignatureRequestEnum
  ) {
    const orderIndex = `${order.orderId}|${signatureRequestType}`;
    const currentValue = this.patientLevelFilters[patientChartId].ordersChecked[
      orderIndex
    ]
      ? true
      : false;
    if (currentValue && !value) {
      this.patientLevelFilters[patientChartId].ordersChecked[orderIndex] =
        false;
      this.numberOfOrdersChecked--;
    } else if (!currentValue && value) {
      this.patientLevelFilters[patientChartId].ordersChecked[orderIndex] =
        order;
      this.numberOfOrdersChecked++;
    }
  }

  /**
   * Allows the provider to sign the selected orders that are not signed
   */
  eSignSelectedOrders() {
    const unsignedCheckedOrders = this.getUnsignedCheckedOrders();
    if (unsignedCheckedOrders.length === 0) {
      const toast = this.toastCtrl.create({
        message: "No unsigned orders selected.",
        duration: 3000,
        position: "below",
        cssClass: "error",
      });
      toast.present();
      return;
    }
    let modal = this.modalCtrl.create(
      ESignSelectedOrdersPopup,
      {
        orders: unsignedCheckedOrders,
        provider: this.credentialedResource,
        popUpTitle: false,
        orderCounter: Object.values(this.getCheckedOrders()).length,
      },
      {
        enableBackdropDismiss: false,
        cssClass: "e-sign-selected-orders",
      }
    );
    modal.present();
    modal.onDidDismiss(
      (data: { bulkUpdateOrderRequest: Observable<void>; cancel: boolean }) => {
        // CO-3363: a modal dismissed without a payload (`dismiss()` with no
        // argument) delivers null here, so reading `.cancel` threw and the
        // spinner below never started — the click looked like it did nothing.
        if (!data || data.cancel || !data.bulkUpdateOrderRequest) {
          return;
        }
        this.increaseSpinnerCount("Submitting e-Signatures...");
        data.bulkUpdateOrderRequest
          .finally(() => this.decreaseSpinnerCount())
          .subscribe(() => {
            const toast = this.toastCtrl.create({
              message:
                "Electronic signing in progress. This will take a moment to complete. Please refresh to see updates.",
              showCloseButton: true,
              position: "below",
              cssClass: "success",
            });
            toast.present();
          }, OrderErrorResponse.handleError(this.toastCtrl, "Failed to submit orders for electronic signature."));
      }
    );
  }

  /**
   * Goes through grouped patients and returns a flattened map of checked orders.
   * @returns Key is orderId and values is OrderResponse.
   */
  getCheckedOrders(): {
    [k: string]: OrderResponse;
  } {
    return Object.values(this.patientLevelFilters).reduce<{
      [k: string]: OrderResponse;
    }>((checkedOrders, patientLevelFilter) => {
      const checkedOrdersForPatient = Object.keys(
        patientLevelFilter.ordersChecked
      )
        .filter((orderId) => patientLevelFilter.ordersChecked[orderId])
        .reduce((checkedOrdersForPatient, orderId) => {
          return {
            ...checkedOrdersForPatient,
            [orderId]: patientLevelFilter.ordersChecked[orderId],
          };
        }, {});
      return {
        ...checkedOrders,
        ...checkedOrdersForPatient,
      };
    }, {});
  }

  onChangeSelectedAction(event: ActionItem | null) {
    if (!event) {
      return;
    }
    if (event.value === "eSignAllOrders") {
      this.onSignAllOrders();
    } else if (event.value === "eSignSelectedOrders") {
      this.eSignSelectedOrders();
    }
    this.selectedAction = null;
    this.selectedActions.clearModel();
  }

  formatPreviousValueDisplay(value: string, changeMadeTo: string) {
    const allowedKeys = ["address1", "address2", "city", "state", "zip", "ZIP"];
    if (changeMadeTo === "Patient Location") {
      var data = JSON.parse(value);
      var formattedData = allowedKeys
        .filter((key) => data[key] !== null && data[key] !== undefined)
        .map((key) => data[key])
        .filter((value) => value !== "-")
        .join(", ");
      return formattedData;
    }
    return value;
  }

  getOrderChecked(
    patientChartId: number,
    orderId: number,
    signatureRequestType: Action.SignatureRequestEnum
  ) {
    return this.patientLevelFilters[patientChartId].ordersChecked[
      `${orderId}|${signatureRequestType}`
    ];
  }

  getUnsignedAllOrders(): OrderIdAndSignatureRequest[] {
    return this.orders.reduce((acc: OrderIdAndSignatureRequest[], order) => {
      let signatureRequests = [];
      if (
        Order.isCreatedOrderSignable(order, this.getCurrentOrderingProviderId())
      ) {
        signatureRequests.push(Action.SignatureRequestEnum.Created);
      }
      if (
        Order.isDiscontinuedOrderSignable(
          order,
          this.getCurrentOrderingProviderId()
        )
      ) {
        signatureRequests.push(Action.SignatureRequestEnum.Discontinued);
      }
      if (signatureRequests.length > 0) {
        acc.push({
          orderId: order.orderId,
          signatureRequests,
        });
      }
      return acc;
    }, []);
  }

  getUnsignedCheckedOrders(): OrderIdAndSignatureRequest[] {
    const checkedOrders = this.getCheckedOrders();
    const orderSignatureRequests: {
      [orderId: string]: Set<Action.SignatureRequestEnum>;
    } = {};
    Object.keys(checkedOrders).forEach((key) => {
      const [orderId, signatureRequest] = key.split("|");
      if (!orderSignatureRequests[orderId]) {
        orderSignatureRequests[orderId] =
          new Set<Action.SignatureRequestEnum>();
      }
      switch (signatureRequest) {
        case Action.SignatureRequestEnum.Created:
          orderSignatureRequests[orderId].add(
            Action.SignatureRequestEnum.Created
          );
          break;
        case Action.SignatureRequestEnum.Discontinued:
          orderSignatureRequests[orderId].add(
            Action.SignatureRequestEnum.Discontinued
          );
          break;
        default:
          break;
      }
    });
    return Object.entries(orderSignatureRequests).map(
      ([orderId, signatureRequestsSet]) => ({
        orderId,
        signatureRequests: Array.from(signatureRequestsSet),
      })
    );
  }

  getOrderCounter(orders: TailoredOrderResponse[]) {
    return orders.reduce((counter: number, order: TailoredOrderResponse) => {
      if (order.showCreatedData) {
        counter++;
      }
      if (order.showDiscontinuedData) {
        counter++;
      }
      return counter;
    }, 0);
  }

  getTotalOrderCounter() {
    return this.orders.reduce((counter: number, order: OrderResponse) => {
      if (
        Order.isCreatedOrderSignable(order, this.getCurrentOrderingProviderId())
      ) {
        counter++;
      }
      if (
        Order.isDiscontinuedOrderSignable(
          order,
          this.getCurrentOrderingProviderId()
        )
      ) {
        counter++;
      }
      return counter;
    }, 0);
  }

  refresh() {
    this.loadData();
  }

  changeTab(tabId: number) {
    this.activatedTabId = tabId;
  }

  emitIFrameErrorBannerCloseButtonClickEvent(event) {
    this.handleIFrameBannerErrorCloseButtonClickEvent.emit(1);
  }
}



// WEBPACK FOOTER //
// ./src/pages/orders-entry-om/provider-panel/provider-panel.ts